<script setup lang="ts">
import { ref } from "vue";
import {
  getCalendarPageItems,
  parseDate,
  type DateDetail,
  getDateDetail,
  getNthMonth,
  flattenArray,
  getNthDay
} from "@zyzgroup/core-common";
import { __color, ColorPattern } from "@zyzgroup/core-web";
import { context, getColors } from "../../context/context";
import Button from "./Button.vue";
import IconArrowRight from "../icon/IconArrowRight.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Button, IconArrowRight },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    colorPattern?: ColorPattern;
    title?: string; // 标题
    initDay?: string; // 当前日期
    startDay?: string; // 可选范围：开始日期
    endDay?: string; // 可选范围：结束日期
    excludeDays?: string[]; // 排除日期范围，要求已按日历排序
    selectedDays?: string[]; // 已选择日期范围，要求已按日历排序
  }>(),
  {
    colorPattern: context.store.getState().color
      .colorPattern as unknown as ColorPattern,
    title: "",
    excludeDays: () => [],
    selectedDays: () => []
  }
);

const emits = defineEmits<{
  (event: "cancel"): void;
  (event: "confirm", date: Date): void;
}>();

// 样式
const {
  foregroundPattern,
  panelPattern,
  primaryPattern,
  secondaryPattern,
  fontPattern,
  fontTertiaryPattern
} = getColors(props.colorPattern);
const panelPatternLighter = __color
  .setColor(panelPattern)
  .lighten(0.5)
  .toString();

const startDateDetail = props.startDay
  ? getDateDetail(parseDate(props.startDay))
  : null;
const endDateDetail = props.endDay
  ? getDateDetail(parseDate(props.endDay))
  : null;
const excludeDateDetails = props.excludeDays.map((d) =>
  getDateDetail(parseDate(d))
);
const initSelectedDateDetails = props.selectedDays.map((d) =>
  getDateDetail(parseDate(d))
);
// 初始化
const initDateDetail = getDateDetail(
  props.initDay ? parseDate(props.initDay) : new Date()
);
const selectDateDetail = ref(getInitAvailableDateDetailInRange(initDateDetail));
let showDateDetail = initDateDetail;
const showYear = ref(showDateDetail.year);
const showMonth = ref(showDateDetail.month);
const showDateDetails = ref(
  expandDateDetails(getCalendarPageItems(showDateDetail.date))
);
setCalendarStyle();

function getInitAvailableDateDetailInRange(
  initDateDetail: DateDetail
): DateDetail | null {
  let currentDateDetail = initDateDetail;
  if (
    startDateDetail &&
    currentDateDetail.date.getTime() < startDateDetail.date.getTime()
  ) {
    currentDateDetail = getDateDetail(startDateDetail.date);
  }

  function available(): boolean {
    for (let i = 0, c = initSelectedDateDetails.length; i < c; i++) {
      const item = initSelectedDateDetails[i];
      if (
        currentDateDetail.year == item.year &&
        currentDateDetail.month == item.month &&
        currentDateDetail.day == item.day
      ) {
        return false;
      }
    }
    for (let i = 0, c = excludeDateDetails.length; i < c; i++) {
      const item = excludeDateDetails[i];
      if (
        currentDateDetail.year == item.year &&
        currentDateDetail.month == item.month &&
        currentDateDetail.day == item.day
      ) {
        return false;
      }
    }
    return true;
  }

  while (!available()) {
    currentDateDetail = getDateDetail(getNthDay(currentDateDetail.date, 1));
    if (
      endDateDetail &&
      currentDateDetail.date.getTime() > endDateDetail.date.getTime()
    ) {
      return null;
    }
  }

  return currentDateDetail;
}

type ExpandedDateDetail = DateDetail & {
  isShowMonth: boolean; // 是否是显示月份
  isInitSelected: boolean; // 初始化时是否已被选择；如果是，则不可再选；
  isDisabled: boolean; // 是否不可选择
  isSelected: boolean; // 是否已被选择
  // 样式
  bgColor: string;
  fontColor: string;
};

function expandDateDetails(pageItems: DateDetail[][]): ExpandedDateDetail[][] {
  const expandedPagesItems: ExpandedDateDetail[][] = [];
  for (let i = 0, rows = pageItems.length; i < rows; i++) {
    const row: ExpandedDateDetail[] = [];
    for (let j = 0; j < 7; j++) {
      const item = pageItems[i][j];
      // 是否已预选
      let isInitSelected = false;
      initSelectedDateDetails.forEach((d) => {
        if (!isInitSelected) {
          isInitSelected =
            item.year == d.year && item.month == d.month && item.day == d.day;
        }
      });
      // 是否不可选
      let isDisabled = isInitSelected;
      if (!isDisabled && startDateDetail) {
        isDisabled = item.date.getTime() < startDateDetail.date.getTime();
      }
      if (!isDisabled && endDateDetail) {
        isDisabled = item.date.getTime() > endDateDetail.date.getTime();
      }
      excludeDateDetails.forEach((d) => {
        if (!isDisabled) {
          isDisabled =
            item.year == d.year && item.month == d.month && item.day == d.day;
        }
      });
      row.push({
        ...item,
        isShowMonth:
          item.year == showYear.value && item.month == showMonth.value,
        isInitSelected,
        isDisabled,
        isSelected:
          !isDisabled &&
          selectDateDetail.value &&
          item.year == selectDateDetail.value.year &&
          item.month == selectDateDetail.value.month &&
          item.day == selectDateDetail.value.day,
        // 样式
        bgColor: "",
        fontColor: ""
      });
    }
    expandedPagesItems.push(row);
  }
  return expandedPagesItems;
}

function setCalendarStyle() {
  flattenArray(showDateDetails.value).forEach((i) => {
    i.bgColor = i.isInitSelected
      ? secondaryPattern
      : i.isSelected
      ? primaryPattern
      : i.isShowMonth
      ? i.isDisabled
        ? panelPatternLighter
        : panelPattern
      : "transparent";
    i.fontColor = i.isInitSelected
      ? fontPattern
      : i.isSelected
      ? foregroundPattern
      : !i.isShowMonth || i.isDisabled
      ? fontTertiaryPattern
      : fontPattern;
  });
  showDateDetails.value = [...showDateDetails.value];
}

// 上一个月
function prevMonth() {
  showDateDetail = getDateDetail(getNthMonth(showDateDetail.date, -1, true));
  showYear.value = showDateDetail.year;
  showMonth.value = showDateDetail.month;
  showDateDetails.value = expandDateDetails(
    getCalendarPageItems(showDateDetail.date)
  );
  setCalendarStyle();
}

// 下一个月
function nextMonth() {
  showDateDetail = getDateDetail(getNthMonth(showDateDetail.date, 1, true));
  showYear.value = showDateDetail.year;
  showMonth.value = showDateDetail.month;
  showDateDetails.value = expandDateDetails(
    getCalendarPageItems(showDateDetail.date)
  );
  setCalendarStyle();
}

// 取消
const cancel = () => {
  emits("cancel");
};

// 确定
const confirm = () => {
  if (!selectDateDetail.value) {
    // 无响应
    return;
  }
  emits("confirm", selectDateDetail.value.date);
};

// 选择日期
const select = (
  year: number,
  month: number,
  day: number,
  isDisabled: boolean
) => {
  if (isDisabled) {
    // 无响应
    return;
  }
  selectDateDetail.value = null;
  let direction = "";
  flattenArray(showDateDetails.value).forEach((i) => {
    const shouldSelected = i.year == year && i.month == month && i.day == day;
    // 可取消选择
    i.isSelected = i.isSelected && shouldSelected ? false : shouldSelected;
    if (i.isSelected) {
      selectDateDetail.value = i;
      // 选择了非显示月日，自动跳转
      if (year != showDateDetail.year || month != showDateDetail.month) {
        direction =
          i.date.getTime() > showDateDetail.date.getTime()
            ? "forward"
            : "backward";
      }
    }
  });
  if (direction == "forward") {
    nextMonth();
  } else if (direction == "backward") {
    prevMonth();
  } else {
    setCalendarStyle();
  }
};

defineExpose({
  //
});
</script>

<template>
  <div
    class="calendar"
    :style="{ backgroundColor: foregroundPattern, color: fontPattern }"
  >
    <div
      class="calendar-title"
      :style="{ borderBottom: `2px solid ${panelPattern}` }"
    >
      <Button
        mode="empty"
        @throttledClick="cancel"
      >
        取消
      </Button>
      <div>{{ props.title }}</div>
      <Button
        :style="{
          color: primaryPattern
        }"
        mode="empty"
        :disabled="!selectDateDetail"
        @throttledClick="confirm"
      >
        确定
      </Button>
    </div>
    <div class="calendar-year-month">
      <Button
        class="icon-prev-month"
        :style="{
          backgroundColor: panelPattern
        }"
        mode="empty"
        @throttledClick="prevMonth"
      >
        <IconArrowRight :color="fontPattern"></IconArrowRight>
      </Button>
      <div>{{ showYear }} 年 {{ showMonth }} 月</div>
      <Button
        class="icon-next-month"
        :style="{
          backgroundColor: panelPattern
        }"
        mode="empty"
        @throttledClick="nextMonth"
      >
        <IconArrowRight :color="fontPattern"></IconArrowRight>
      </Button>
    </div>
    <div class="calendar-items">
      <div
        class="calendar-items-row calendar-items-row-week"
        :style="{ color: fontTertiaryPattern }"
      >
        <div>一</div>
        <div>二</div>
        <div>三</div>
        <div>四</div>
        <div>五</div>
        <div>六</div>
        <div>日</div>
      </div>
      <div
        v-for="row in showDateDetails"
        class="calendar-items-row"
      >
        <Button
          v-for="item in row"
          class="calendar-item"
          :style="{
            backgroundColor: item.bgColor,
            color: item.fontColor
          }"
          mode="empty"
          :disabled="item.isDisabled"
          @throttledClick="
            select(item.year, item.month, item.day, item.isDisabled)
          "
        >
          {{ item.day }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.calendar {
  width: 100%;
  box-sizing: border-box;
  font-size: 1em;
  line-height: 1;
}
.calendar > div {
  padding: 0.8em 1em;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.calendar > .calendar-title {
  font-size: 0.8em;
}
.calendar > .calendar-title > div:not(:nth-child(2)) {
  padding: 0.3em 0.6em;
}
.calendar > .calendar-title > div:nth-child(2) {
  font-weight: bold;
  font-size: 1em;
}
.calendar > .calendar-year-month {
  font-size: 0.8em;
}
.calendar > .calendar-year-month > div:nth-child(2) {
  font-weight: bold;
  font-size: 1em;
}
.icon-prev-month,
.icon-next-month {
  width: 1.8em;
  border-radius: 1.8em;
  overflow: hidden;
  padding: 0.2em;
}
.icon-prev-month {
  transform: rotate(180deg);
}
.calendar > .calendar-items {
  flex-direction: column;
  padding-top: 0.5em;
}
.calendar-items-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  font-size: 0.8em;
}
.calendar-items-row-week {
  padding-bottom: 0.5em;
  font-size: 0.7em;
}
.calendar-items-row-week > div {
  flex: 1;
}
.calendar-item {
  flex: 1;
  margin-bottom: 0.3em;
  padding: 0.6em 0;
  border-radius: 0.4em;
  font-weight: bold;
}
.calendar-item + .calendar-item {
  margin-left: 0.3em;
}
</style>
